import React, { useEffect } from 'react';

const ASI1Fast = () => {
  // Ensure scroll position is at the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="container mx-auto sm:px-6 md:px-4 sm:py-8 max-w-7xl" style={{ paddingLeft: '0.1rem', paddingRight: '0.1rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-12 text-white">
          <h1 className="text-4xl font-bold mb-4">ASI 1-Fast</h1>
          <p className="text-xl opacity-90">
            Ultra-Low-Latency Reasoning & Lightning-Quick Agent Discovery
          </p>
        </div>

        {/* Documentation content */}
        <div className="px-6 md:px-12 lg:px-16 py-8">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              ASI 1-Fast is our speed-first, closed-source reasoning model—purpose-built for situations where every millisecond counts. Whether you're matching user intents to a fleet of micro-agents, running real-time compliance checks inside a smart-contract wallet, or powering voice assistants that must never lag, ASI 1-Fast delivers state-of-the-art reasoning with the shortest response times in our lineup.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Performance Snapshot</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 rounded-lg">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="text-left p-4 border-b font-semibold">Metric</th>
                      <th className="text-left p-4 border-b font-semibold">ASI 1-Fast</th>
                      <th className="text-left p-4 border-b font-semibold">ASI 1-Mini</th>
                      <th className="text-left p-4 border-b font-semibold">ASI 1-Mini-Extended*</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4 border-b">MMLU Benchmark</td>
                      <td className="p-4 border-b font-bold text-blue-700">87 %</td>
                      <td className="p-4 border-b">85 %</td>
                      <td className="p-4 border-b">89 %</td>
                    </tr>
                    <tr>
                      <td className="p-4">Context Window</td>
                      <td className="p-4 font-bold text-blue-700">24 k tokens (speed-optimised)</td>
                      <td className="p-4">32 k</td>
                      <td className="p-4">64 k</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600 mt-4">*Shown purely for comparison; Mini-Extended remains the heavyweight auditor.</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Why Choose ASI 1-Fast?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Blazing Inference</h3>
                <p className="text-gray-700">
                  Kernel fusion, operator pruning, and speculative decoding yield up-to-2× faster answers than ASI 1-Mini with zero warm-up lag.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Instant Agent Discovery</h3>
                <p className="text-gray-700">
                  Purpose-built head layers map user requests to an unlimited registry of tool signatures in a single forward pass, returning a ranked list plus confidence scores.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Advanced Reasoning at Sprint Speed</h3>
                <p className="text-gray-700">
                  Retains deep-chain reasoning ability (proof-by-construction tasks, multi-hop retrieval) yet executes those chains with micro-planning shortcuts.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Adaptive Budgeting</h3>
                <p className="text-gray-700">
                  Built-in latency controller caps per-request time; the model trims reasoning depth on the fly to guarantee SLAs when traffic spikes.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Safety Filters On Board</h3>
                <p className="text-gray-700">
                  Compact guardrails block jailbreak patterns and common smart-contract exploits without slowing inference.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Prime Use-Cases</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 rounded-lg">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="text-left p-4 border-b font-semibold">Domain</th>
                      <th className="text-left p-4 border-b font-semibold">How ASI 1-Fast Excels</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4 border-b font-medium">Voice & Multimodal Assistants</td>
                      <td className="p-4 border-b">Sub-conversation-latency responses keep interactions natural—even on device.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">High-Frequency Trading & DeFi Bots</td>
                      <td className="p-4 border-b">Evaluates market moves, validates contract calls, and selects strategies within block times.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">Real-Time Moderation Pipelines</td>
                      <td className="p-4 border-b">Performs classification and policy reasoning inline, adding virtually no user-visible delay.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">Dynamic Content Personalisation</td>
                      <td className="p-4 border-b">Generates adaptive UI copy or recommendations while the page is still loading.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">Industrial Control & IoT</td>
                      <td className="p-4">Translates sensor data into safety-checked actuation commands under tight timing constraints.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Trust & Safety</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 rounded-lg">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="text-left p-4 border-b font-semibold">Capability</th>
                      <th className="text-left p-4 border-b font-semibold">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4 border-b font-medium">Low Hallucination Footprint</td>
                      <td className="p-4 border-b">≤ 1 % unsupported claims in internal evals after consistency checks.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">Security Reflexes</td>
                      <td className="p-4">Hard-coded pattern blockers for re-entrancy, integer-overflow, and oracle-desync exploits.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mt-6">
              Utilize ASI 1-Fast when every millisecond counts—then pair it with ASI 1-Mini for general workloads or ASI 1-Mini-Extended for deep audits, letting each closed-source model excel where it performs best.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ASI1Fast;
